/**
 * Entity API Endpoints
 *
 * Client functions for entity-related database operations.
 * These functions are used by React Query hooks.
 */

import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type {
  SaveEntityRequest,
  SaveEntityResponse,
  UpdateEntityFieldRequest,
  UpdateEntityFieldResponse,
} from "../types/entity";

type EntityDataDB = Database["public"]["Tables"]["entity_data"]["Insert"];
type EntityDataUpdate = Database["public"]["Tables"]["entity_data"]["Update"];

export async function saveEntity(
  request: SaveEntityRequest
): Promise<SaveEntityResponse> {
  try {
    const { formSessionId, entityData } = request;

    // Upsert entity data to database
    const { data: savedEntity, error: entityError } = await supabase
      .from("entity_data")
      .upsert(
        {
          form_session_id: formSessionId,
          ...(entityData as unknown as EntityDataDB),
        },
        {
          onConflict: "form_session_id",
        }
      )
      .select()
      .single();

    if (entityError) {
      console.error("❌ [Entity] Error saving entity data:", entityError);
      return {
        success: false,
        error: entityError.message || "Failed to save entity data",
      };
    }

    console.log("✅ [Entity] Successfully saved entity data:", savedEntity.id);

    return {
      success: true,
      entityId: savedEntity.id,
    };
  } catch (error) {
    console.error("❌ [Entity] Unexpected error saving entity:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Updates specific fields of an existing entity
 * Uses Supabase .update() for partial updates without replacing the entire record
 */
export async function updateEntityField(
  request: UpdateEntityFieldRequest
): Promise<UpdateEntityFieldResponse> {
  try {
    const { formSessionId, fieldUpdates } = request;

    // Update only the specified fields
    const { data: updatedEntity, error: updateError } = await supabase
      .from("entity_data")
      .update(fieldUpdates as unknown as EntityDataUpdate)
      .eq("form_session_id", formSessionId)
      .select()
      .single();

    if (updateError) {
      console.error("❌ [Entity] Error updating entity fields:", updateError);
      return {
        success: false,
        error: updateError.message || "Failed to update entity fields",
      };
    }

    if (!updatedEntity) {
      console.error(
        "❌ [Entity] No entity found with form_session_id:",
        formSessionId
      );
      return {
        success: false,
        error: "Entity not found",
      };
    }

    console.log(
      "✅ [Entity] Successfully updated entity fields:",
      updatedEntity.id
    );

    return {
      success: true,
      entityId: updatedEntity.id,
    };
  } catch (error) {
    console.error("❌ [Entity] Unexpected error updating entity:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}
